import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, X, Barcode, Check, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface BarcodeResult {
  value: string;
  format: string;
  confidence: number;
  productName?: string; // From EAN database lookup
  isKnown: boolean;
}

// EAN Database - loaded from CSV
let eanDatabase: Map<string, string> | null = null;

async function loadEANDatabase(): Promise<Map<string, string>> {
  if (eanDatabase) return eanDatabase;

  try {
    // Load EAN database from CSV file
    const response = await fetch('/KIEHLS_EANs.csv');
    const csvText = await response.text();

    eanDatabase = new Map();
    const lines = csvText.split('\n');

    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      // Parse CSV line (EAN is column 5, name is column 6)
      const columns = line.split(',');
      if (columns.length >= 6) {
        const ean = columns[4]?.trim();
        const name = columns[5]?.trim();
        if (ean && name) {
          eanDatabase.set(ean, name);
        }
      }
    }

    console.log(`[EAN] Loaded ${eanDatabase.size} products from database`);
    return eanDatabase;
  } catch (err) {
    console.error('[EAN] Failed to load database:', err);
    return new Map();
  }
}

function lookupEAN(ean: string, db: Map<string, string>): string | undefined {
  // Try exact match first
  if (db.has(ean)) return db.get(ean);

  // Try without leading zeros
  const trimmed = ean.replace(/^0+/, '');
  let result: string | undefined;
  db.forEach((value, key) => {
    if (key.replace(/^0+/, '') === trimmed) {
      result = value;
    }
  });

  return result;
}

const DebugCameraView: React.FC = () => {
  const navigate = useNavigate();
  const [capturedImages, setCapturedImages] = useState<{left?: string, right?: string}>({});
  const [liveFrames, setLiveFrames] = useState<{left?: string, right?: string}>({});
  const [isLivePreview, setIsLivePreview] = useState(true);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string>('');
  const [lastCaptureInfo, setLastCaptureInfo] = useState<string>('');
  const [barcodeResults, setBarcodeResults] = useState<BarcodeResult[]>([]);
  const [isScanningBarcode, setIsScanningBarcode] = useState(false);
  const [eanDb, setEanDb] = useState<Map<string, string>>(new Map());

  // Load EAN database on mount
  useEffect(() => {
    loadEANDatabase().then(db => setEanDb(db));
  }, []);

  // Live camera preview - fetch frames from both cameras
  useEffect(() => {
    if (!isLivePreview) return;

    let isMounted = true;
    let frameCount = 0;

    const fetchFrames = async () => {
      if (!isMounted || !isLivePreview) return;

      try {
        // Fetch from both cameras in parallel - direct to Jetson for speed
        const [leftResponse, rightResponse] = await Promise.all([
          fetch(`http://192.168.55.1:8000/api/camera/preview?camera=0&t=${Date.now()}`),
          fetch(`http://192.168.55.1:8000/api/camera/preview?camera=2&t=${Date.now()}`)
        ]);

        const [leftData, rightData] = await Promise.all([
          leftResponse.json(),
          rightResponse.json()
        ]);

        if (isMounted && isLivePreview) {
          const newFrames: {left?: string, right?: string} = {};

          if (leftData.frame) {
            newFrames.left = leftData.frame.startsWith('data:image')
              ? leftData.frame
              : `data:image/jpeg;base64,${leftData.frame}`;
          }

          if (rightData.frame) {
            newFrames.right = rightData.frame.startsWith('data:image')
              ? rightData.frame
              : `data:image/jpeg;base64,${rightData.frame}`;
          }

          setLiveFrames(newFrames);
          frameCount++;
        }
      } catch (err) {
        console.error('[LIVE] Frame fetch error:', err);
      }

      // Continue fetching if still in live mode
      if (isMounted && isLivePreview) {
        setTimeout(fetchFrames, 100); // ~10 FPS
      }
    };

    fetchFrames();

    return () => {
      isMounted = false;
    };
  }, [isLivePreview]);

  const handleBack = () => {
    navigate('/');
  };

  const scanBarcode = async (): Promise<BarcodeResult[]> => {
    try {
      setIsScanningBarcode(true);
      const response = await fetch(`http://localhost:8001/api/detect?model=dynamsoft&t=${Date.now()}`);
      const data = await response.json();

      if (data.success && data.barcodes && data.barcodes.length > 0) {
        // Process barcodes and look up in EAN database
        const results: BarcodeResult[] = data.barcodes.map((bc: any) => {
          const productName = lookupEAN(bc.value, eanDb);
          return {
            value: bc.value,
            format: bc.format,
            confidence: bc.confidence,
            productName,
            isKnown: !!productName
          };
        });
        return results;
      }
      return [];
    } catch (err) {
      console.error('[BARCODE] Scan error:', err);
      return [];
    } finally {
      setIsScanningBarcode(false);
    }
  };

  const handleCapture = async () => {
    console.log('[DEBUG] Capture button clicked at:', new Date().toLocaleTimeString());
    setIsCapturing(true);
    setError('');
    setBarcodeResults([]);
    setLastCaptureInfo('Capturing from both cameras...');

    try {
      // Run camera capture and barcode scan in parallel
      const [captureResponse, barcodes] = await Promise.all([
        fetch(`http://localhost:8001/api/camera/capture?t=${Date.now()}`),
        scanBarcode()
      ]);

      const captureData = await captureResponse.json();

      // Update barcode results
      setBarcodeResults(barcodes);

      if (!captureData.success) {
        setError(captureData.error || 'Failed to capture images');
        setIsCapturing(false);
        return;
      }

      console.log('[DEBUG] Captures:', captureData.captures);
      console.log('[DEBUG] Barcodes found:', barcodes);

      // Read back both images from disk
      const images: {left?: string, right?: string} = {};

      for (const capture of captureData.captures || []) {
        const readResponse = await fetch(`http://localhost:8001/api/camera/read?filepath=${encodeURIComponent(capture.filepath)}`);
        const readData = await readResponse.json();

        if (readData.success && readData.image) {
          const imageData = readData.image.startsWith('data:image')
            ? readData.image
            : `data:image/jpeg;base64,${readData.image}`;

          if (capture.camera === 'left') {
            images.left = imageData;
          } else if (capture.camera === 'right') {
            images.right = imageData;
          }
        }
      }

      setCapturedImages(images);

      const captureCount = Object.keys(images).length;
      const timestamp = new Date().toLocaleTimeString();
      const barcodeInfo = barcodes.length > 0
        ? ` | ${barcodes.length} barcode${barcodes.length !== 1 ? 's' : ''} found`
        : ' | No barcodes';
      setLastCaptureInfo(`Captured ${captureCount} image${captureCount !== 1 ? 's' : ''} at ${timestamp}${barcodeInfo}`);

      if (captureData.errors && captureData.errors.length > 0) {
        console.warn('[DEBUG] Capture warnings:', captureData.errors);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to capture');
      console.error('[DEBUG] Capture exception:', err);
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Camera className="text-blue-400" size={28} />
          <div>
            <h1 className="text-white text-2xl font-semibold">Debug Camera</h1>
            <p className="text-gray-400 text-sm">Development Tool - Camera Preview & Capture</p>
          </div>
        </div>

        <button
          onClick={handleBack}
          className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <X size={20} />
          <span>Close</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
        {/* Live/Captured Toggle */}
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => setIsLivePreview(true)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isLivePreview
                ? 'bg-green-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Live Preview
          </button>
          <button
            onClick={() => setIsLivePreview(false)}
            disabled={!capturedImages.left && !capturedImages.right}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              !isLivePreview
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            } ${!capturedImages.left && !capturedImages.right ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Last Capture
          </button>
        </div>

        {/* Dual Camera Display */}
        <div className="flex gap-4 w-full max-w-6xl">
          {/* Left Camera */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="relative flex-1 bg-black rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-700"
          >
            <div className="absolute top-2 left-2 bg-blue-600 px-3 py-1 rounded-full z-10">
              <span className="text-white text-sm font-semibold">Left Camera</span>
            </div>
            {isLivePreview && liveFrames.left ? (
              <img
                src={liveFrames.left}
                alt="Left Camera Live"
                className="w-full h-full object-contain"
                style={{ imageRendering: 'auto' }}
              />
            ) : !isLivePreview && capturedImages.left ? (
              <img
                src={capturedImages.left}
                alt="Left Camera Captured"
                className="w-full h-full object-contain"
                style={{ imageRendering: 'auto' }}
              />
            ) : (
              <div className="w-full h-[360px] flex items-center justify-center">
                <div className="text-center">
                  <Camera className="text-gray-600 mx-auto mb-4" size={48} />
                  <p className="text-gray-400 text-sm">{isLivePreview ? 'Connecting...' : 'No capture'}</p>
                </div>
              </div>
            )}
          </motion.div>

          {/* Right Camera */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="relative flex-1 bg-black rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-700"
          >
            <div className="absolute top-2 left-2 bg-green-600 px-3 py-1 rounded-full z-10">
              <span className="text-white text-sm font-semibold">Right Camera</span>
            </div>
            {isLivePreview && liveFrames.right ? (
              <img
                src={liveFrames.right}
                alt="Right Camera Live"
                className="w-full h-full object-contain"
                style={{ imageRendering: 'auto' }}
              />
            ) : !isLivePreview && capturedImages.right ? (
              <img
                src={capturedImages.right}
                alt="Right Camera Captured"
                className="w-full h-full object-contain"
                style={{ imageRendering: 'auto' }}
              />
            ) : (
              <div className="w-full h-[360px] flex items-center justify-center">
                <div className="text-center">
                  <Camera className="text-gray-600 mx-auto mb-4" size={48} />
                  <p className="text-gray-400 text-sm">{isLivePreview ? 'Connecting...' : 'No capture'}</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/80 backdrop-blur-sm px-6 py-4 rounded-lg shadow-xl max-w-md">
            <p className="text-white font-semibold mb-2">Capture Error</p>
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        {/* Capture Status */}
        {lastCaptureInfo && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="px-4 py-2 rounded-lg bg-green-800/80 text-green-100"
          >
            <p className="text-sm font-medium">{lastCaptureInfo}</p>
          </motion.div>
        )}

        {/* Barcode Results */}
        {barcodeResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-2xl bg-gray-800 rounded-xl p-4 shadow-xl border border-gray-700"
          >
            <div className="flex items-center gap-2 mb-3">
              <Barcode className="text-purple-400" size={24} />
              <h3 className="text-white font-semibold text-lg">Barcode Detection Results</h3>
            </div>
            <div className="space-y-2">
              {barcodeResults.map((bc, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    bc.isKnown ? 'bg-green-900/50 border border-green-700' : 'bg-yellow-900/50 border border-yellow-700'
                  }`}
                >
                  {bc.isKnown ? (
                    <Check className="text-green-400 flex-shrink-0" size={20} />
                  ) : (
                    <AlertCircle className="text-yellow-400 flex-shrink-0" size={20} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="text-white font-mono text-lg">{bc.value}</code>
                      <span className="text-gray-400 text-xs px-2 py-0.5 bg-gray-700 rounded">{bc.format}</span>
                    </div>
                    {bc.isKnown ? (
                      <p className="text-green-300 text-sm mt-1 truncate">
                        {bc.productName}
                      </p>
                    ) : (
                      <p className="text-yellow-300 text-sm mt-1">
                        Unknown product (not in Kiehl's database)
                      </p>
                    )}
                  </div>
                  <span className="text-gray-400 text-xs">{bc.confidence}%</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Capture Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleCapture}
          disabled={isCapturing}
          className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all ${
            isCapturing
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-500 text-white'
          }`}
        >
          <Camera size={24} />
          <span>{isCapturing ? 'Capturing...' : 'Capture Photo'}</span>
        </motion.button>

        {/* Info Text */}
        <div className="text-center text-gray-400 text-sm max-w-md">
          <p>Photos are saved to:</p>
          <code className="text-gray-300 bg-gray-800 px-2 py-1 rounded mt-1 inline-block">
            ~/Documents/kiosk_data_capture/YYYY-MM-DD/
          </code>
          <p className="mt-2 text-xs">
            Each capture saves both cameras with filenames:<br/>
            <code className="text-gray-500">capture_YYYYMMDD_HHMMSS_left.jpg</code> and <code className="text-gray-500">capture_YYYYMMDD_HHMMSS_right.jpg</code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DebugCameraView;
